import { useId } from "react";

import { tv, type VariantProps } from "tailwind-variants";

export const loaderLogoVariants = tv({
	base: "text-default",
	variants: {
		size: {
			xs: "h-8 w-5",
			sm: "h-10 w-6",
			md: "h-11 w-7",
			lg: "h-24 w-16",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

export type LoaderLogoVariant = VariantProps<typeof loaderLogoVariants>;

export interface LoaderLogoProps extends LoaderLogoVariant {
	readonly className?: string;
	readonly isShiny?: boolean;
}

export function LoaderLogo({ size = "md", className, isShiny = true }: LoaderLogoProps) {
	const gradientId = useId();

	const clipId = `${gradientId}-clip`;

	return (
		<svg
			viewBox="0 0 110 170"
			className={loaderLogoVariants({ size, className })}
			aria-hidden="true"
		>
			{isShiny && (
				<defs>
					<linearGradient
						id={gradientId}
						gradientUnits="userSpaceOnUse"
						x1="-110"
						y1="40"
						x2="0"
						y2="120"
					>
						<stop offset="0" stopColor="white" stopOpacity="0" />
						<stop offset="0.35" stopColor="white" stopOpacity="0" />
						<stop offset="0.5" stopColor="white" stopOpacity="0.9" />
						<stop offset="0.65" stopColor="white" stopOpacity="0" />
						<stop offset="1" stopColor="white" stopOpacity="0" />
						<animate
							attributeName="x1"
							values="-110; 110; 110"
							keyTimes="0; 0.5; 1"
							keySplines="0.42 0 0.58 1; 0 0 1 1"
							calcMode="spline"
							dur="3.4s"
							repeatCount="indefinite"
						/>
						<animate
							attributeName="x2"
							values="0; 220; 220"
							keyTimes="0; 0.5; 1"
							keySplines="0.42 0 0.58 1; 0 0 1 1"
							calcMode="spline"
							dur="3.4s"
							repeatCount="indefinite"
						/>
					</linearGradient>
					<clipPath id={clipId}>
						<path d="M 46 17.5 L 46 153.6 L 10.8 116.9 Z" />
						<path d="M 64 17.5 L 99.2 116.9 L 64 153.6 Z" />
					</clipPath>
				</defs>
			)}
			<path
				d="M 46 17.5 L 46 153.6 L 10.8 116.9 Z"
				fill="currentColor"
				strokeLinejoin="round"
				stroke="currentColor"
				strokeWidth="6"
			/>
			<path
				d="M 64 17.5 L 99.2 116.9 L 64 153.6 Z"
				fill="currentColor"
				strokeLinejoin="round"
				stroke="currentColor"
				strokeWidth="6"
			/>
			{isShiny && (
				<rect
					x="0"
					y="0"
					width="110"
					height="170"
					fill={`url(#${gradientId})`}
					clipPath={`url(#${clipId})`}
				/>
			)}
		</svg>
	);
}
